import { useCallback, useEffect, useRef, useState } from 'react';
const addScrollBlocking = (preventScrolling, stopPropagation, ref) => {
  document.body.addEventListener('touchmove', preventScrolling, {
    passive: false
  });
  ref.current?.addEventListener('touchmove', stopPropagation);
  document.body.style.overflow = 'hidden';
};
const removeScrollBlocking = (preventScrolling, stopPropagation, ref) => {
  document.body.removeEventListener('touchmove', preventScrolling);
  ref.current?.removeEventListener('touchmove', stopPropagation);
  document.body.style.overflow = 'inherit';
};

/**
 * Disables scrolling when passed `true` or an array where all items are `true`.
 *
 * Returns an optional callback ref. Pass this to an element if it or its children
 * should allow touch-based scrolling within that element's bounds.
 *
 * @param {boolean | boolean[]} conditionProps
 * @returns
 */
const useScrollBlocking = conditionProps => {
  // useRef refs are null on first render and don't trigger a re-render when they get their
  // element. Force re-run when ref mounts to ensure the stopPropagation listener is attached.
  const ref = useRef();
  const [refIsMounted, setRefIsMounted] = useState(false);
  const callbackRef = useCallback(element => {
    ref.current = element;
    setRefIsMounted(Boolean(element));
  }, []);
  const conditionsMet = Array.isArray(conditionProps) ? conditionProps.every(condition => condition) : Boolean(conditionProps);
  const preventScrolling = useCallback(event => event.preventDefault(), []);
  const stopPropagation = useCallback(event => event.stopPropagation(), []);
  useEffect(() => {
    const cleanup = () => removeScrollBlocking(preventScrolling, stopPropagation, ref);
    if (conditionsMet) {
      addScrollBlocking(preventScrolling, stopPropagation, ref);
    } else {
      cleanup();
    }
    return cleanup;
    // preventScrolling and stopPropagation are stable callbacks with no deps, so this
    // will re-run when conditionsMet or refIsMounted flip between true and false.
  }, [preventScrolling, conditionsMet, stopPropagation, refIsMounted]);
  return callbackRef;
};
export default useScrollBlocking;